import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { observer } from 'mobx-react';
import { appConfigStore } from 'Models/AppConfigStore';
import { stringNotEmpty } from 'Util/TypeGuards';
import { BookingDetailSection, SectionProps } from '../../SharedSections/BookingDetailSection';
import { useDynamicBookingFieldsStore, getFieldById } from 'Stores/DynamicBookingFieldsStore';
import { useDynamicBookingSectionsStore } from 'Stores/DynamicBookingSectionsStore';
import { getMessageByKey } from 'Stores/LanguageStore';

/**
 * Renders the original booking note plus admin-configured dynamic field values
 * stored on the alteration, together in the "Additional information" section.
 * Always shows historical dynamic values even if the field has since been
 * archived — the field record is still looked up (archived entries remain in the
 * cache) so the label resolves correctly.
 */
export const DynamicFieldsInfoSection = observer(({ booking, canEdit, onEdit, isInvoiced }: SectionProps) => {
	const intl = useIntl();
	const { fields, loaded, loadFields } = useDynamicBookingFieldsStore();
	const { sections, loaded: sectionsLoaded, loadSections } = useDynamicBookingSectionsStore();

	useEffect(() => {
		if (!loaded) loadFields();
	}, [loaded, loadFields]);
	useEffect(() => {
		if (!sectionsLoaded) loadSections();
	}, [sectionsLoaded, loadSections]);

	// The original free-text booking note ("Additional information"), shown
	// alongside the dynamic fields when enabled and present. Mirrors the booking
	// wizard, where the note now lives in the same section.
	const note = booking.bookedSummary?.note ?? '';
	const showNote = appConfigStore.bookingNoteEnabled && stringNotEmpty(note);
	const noteLabelKey = booking.hasVehicle()
		? 'vehicle-tab__additional-information__label'
		: 'passenger-booking__additional-information__label';
	const noteLabel = getMessageByKey(noteLabelKey, 'Additional information') ?? 'Additional information';

	const dynamicEntries: Array<{ fieldId: string; value: string }> = (() => {
		const ctx = (booking as any)?.bookedSummary?.alterationContext;
		const raw = ctx?.dynamicFieldValues;
		if (!raw) return [];
		if (Array.isArray(raw)) {
			// Newtonsoft serialises List<DynamicFieldValueEntry> as [{ fieldId, value }, …]
			return raw
				.filter((e: any) => e && e.fieldId)
				.map((e: any) => ({ fieldId: String(e.fieldId), value: String(e.value ?? '') }));
		}
		if (typeof raw === 'object') {
			return Object.entries(raw).map(([fieldId, value]) => ({ fieldId, value: String(value ?? '') }));
		}
		return [];
	})();

	// Skip entries whose field has since been archived — archived fields are
	// hidden everywhere in the app, including historical bookings.
	const nonEmpty = dynamicEntries.filter(e => {
		if (!e.value || e.value.trim() === '') return false;
		const field = getFieldById(fields, e.fieldId);
		if (!field) return false;
		if (field.archived) return false;
		return true;
	});
	if (nonEmpty.length === 0 && !showNote) return null;

	// Active (non-archived) sections keyed by id. Fields assigned to an archived or
	// removed section fall back to the "Additional information" card.
	const activeSectionById = new Map(sections.map(s => [s.id, s]));

	const renderEntry = (entry: { fieldId: string; value: string }) => {
		const field = getFieldById(fields, entry.fieldId);
		const label = field?.labelKey
			? (getMessageByKey(field.labelKey, field.label) ?? field.label)
			: (field?.label ?? '(removed field)');
		// TOGGLE values are stored as 'true'/'false' — render them as Yes/No.
		let displayValue = entry.value;
		if (field?.fieldType === 'TOGGLE') {
			displayValue = entry.value === 'true' ? 'Yes' : 'No';
		}
		return (
			<div key={entry.fieldId}>
				<dt className="text-sm text-gray-500">{label}</dt>
				<dd className="text-sm">{displayValue}</dd>
			</div>
		);
	};

	const grid = (entries: Array<{ fieldId: string; value: string }>, includeNote: boolean) => (
		<dl className="booking-dynamic-fields grid grid-cols-1 md:grid-cols-2 gap-4">
			{entries.map(renderEntry)}
			{includeNote && showNote && (
				<div>
					<dt className="text-sm text-gray-500">{noteLabel}</dt>
					<dd className="text-sm">{note}</dd>
				</div>
			)}
		</dl>
	);

	// Group the values by their field's assigned (non-archived) section; everything
	// else goes into the default "Additional information" card with the note.
	const bySection = new Map<string, Array<{ fieldId: string; value: string }>>();
	const ungrouped: Array<{ fieldId: string; value: string }> = [];
	nonEmpty.forEach(entry => {
		const field = getFieldById(fields, entry.fieldId);
		const section = field?.sectionId ? activeSectionById.get(field.sectionId) : undefined;
		if (section) {
			if (!bySection.has(section.id)) bySection.set(section.id, []);
			bySection.get(section.id)!.push(entry);
		} else {
			ungrouped.push(entry);
		}
	});

	const orderedSections = [...sections]
		.filter(s => bySection.has(s.id))
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	const editRules = { canEdit, editType: 'add-on' as const, onEdit, isInvoiced };

	return (
		<>
			{orderedSections.map(section => {
				const name = section.labelKey
					? (getMessageByKey(section.labelKey, section.label) ?? section.label)
					: section.label;
				return (
					<BookingDetailSection
						key={section.id}
						sectionName={name}
						sectionContent={grid(bySection.get(section.id) ?? [], false)}
						sectionClassName="dynamic-fields"
						icon={{ name: section.icon || 'list_alt', useMaterialIcons: true }}
						modificationRules={editRules}
					/>
				);
			})}
			{(ungrouped.length > 0 || showNote) && (
				<BookingDetailSection
					sectionName={intl.formatMessage({
						id: 'booking-detail__dynamic-fields__label',
						defaultMessage: 'Additional information',
					})}
					sectionContent={grid(ungrouped, true)}
					sectionClassName="dynamic-fields"
					icon={{ name: 'list_alt', useMaterialIcons: true }}
					modificationRules={editRules}
				/>
			)}
		</>
	);
});
